/**
 * useStoreMemory Hook
 * Custom hook for managing store memory notes
 * Supports real-time updates via WebSocket
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import { useAppSelector } from '@/store/hooks'
import { storeMemoryService, MAX_STORE_NOTES } from '@/services/storeMemoryService'
import socketService, { StoreMemoryUpdateData } from '@/services/socketService'
import {
  StoreMemoryWithCreator,
  CreateStoreMemoryRequest,
  UpdateStoreMemoryRequest,
  StoreMemoryCategory,
  STORE_MEMORY_CATEGORIES
} from '@shared/types'

interface UseStoreMemoryOptions {
  storeId?: string // If not provided, uses user's branchId
  autoFetch?: boolean
}

interface UseStoreMemoryReturn {
  memories: StoreMemoryWithCreator[]
  loading: boolean
  error: string | null
  activeCount: number
  maxNotes: number
  canAddMore: boolean
  categories: StoreMemoryCategory[]
  fetchMemories: () => Promise<void>
  createMemory: (data: Omit<CreateStoreMemoryRequest, 'storeId'>) => Promise<StoreMemoryWithCreator>
  updateMemory: (id: string, data: UpdateStoreMemoryRequest) => Promise<StoreMemoryWithCreator>
  deleteMemory: (id: string) => Promise<void>
  refreshCount: () => Promise<void>
}

export function useStoreMemory(options: UseStoreMemoryOptions = {}): UseStoreMemoryReturn {
  const { autoFetch = true } = options
  const user = useAppSelector((state) => state.auth.user)

  // Determine the store ID to use
  const storeId = options.storeId || user?.branchId || ''

  const [memories, setMemories] = useState<StoreMemoryWithCreator[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [activeCount, setActiveCount] = useState(0)
  const [maxNotes, setMaxNotes] = useState(MAX_STORE_NOTES)

  const canAddMore = activeCount < maxNotes
  const categories = STORE_MEMORY_CATEGORIES

  /**
   * Fetch memories for the store
   */
  const fetchMemories = useCallback(async () => {
    if (!storeId) {
      setError('No store ID available')
      return
    }

    setLoading(true)
    setError(null)

    try {
      const data = await storeMemoryService.list(storeId)
      setMemories(data)
      setActiveCount(data.filter(m => m.isActive).length)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch memories'
      setError(errorMessage)
      console.error('Failed to fetch store memories:', err)
    } finally {
      setLoading(false)
    }
  }, [storeId])

  /**
   * Refresh active count
   */
  const refreshCount = useCallback(async () => {
    if (!storeId) return

    try {
      const countData = await storeMemoryService.getCount(storeId)
      setActiveCount(countData.activeCount)
      setMaxNotes(countData.maxNotes)
    } catch (err) {
      console.error('Failed to refresh count:', err)
    }
  }, [storeId])

  /**
   * Create a new memory
   */
  const createMemory = useCallback(async (
    data: Omit<CreateStoreMemoryRequest, 'storeId'>
  ): Promise<StoreMemoryWithCreator> => {
    if (!storeId) {
      throw new Error('No store ID available')
    }

    if (!canAddMore) {
      throw new Error(`Maximum notes limit (${maxNotes}) reached. Please delete or archive existing notes.`)
    }

    setLoading(true)
    setError(null)

    try {
      const newMemory = await storeMemoryService.create({
        ...data,
        storeId
      })
      setMemories(prev => [newMemory, ...prev])
      setActiveCount(prev => prev + 1)
      return newMemory
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create memory'
      setError(errorMessage)
      throw err
    } finally {
      setLoading(false)
    }
  }, [storeId, canAddMore, maxNotes])

  /**
   * Update an existing memory
   */
  const updateMemory = useCallback(async (
    id: string,
    data: UpdateStoreMemoryRequest
  ): Promise<StoreMemoryWithCreator> => {
    setLoading(true)
    setError(null)

    try {
      const updatedMemory = await storeMemoryService.update(id, data)
      setMemories(prev =>
        prev.map(m => m.id === id ? updatedMemory : m)
      )

      // Update active count if isActive changed
      if (data.isActive !== undefined) {
        const wasActive = memories.find(m => m.id === id)?.isActive
        if (wasActive !== data.isActive) {
          setActiveCount(prev => data.isActive ? prev + 1 : prev - 1)
        }
      }

      return updatedMemory
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update memory'
      setError(errorMessage)
      throw err
    } finally {
      setLoading(false)
    }
  }, [memories])

  /**
   * Delete a memory
   */
  const deleteMemory = useCallback(async (id: string): Promise<void> => {
    setLoading(true)
    setError(null)

    try {
      await storeMemoryService.delete(id)
      const deletedMemory = memories.find(m => m.id === id)
      setMemories(prev => prev.filter(m => m.id !== id))
      if (deletedMemory?.isActive) {
        setActiveCount(prev => prev - 1)
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete memory'
      setError(errorMessage)
      throw err
    } finally {
      setLoading(false)
    }
  }, [memories])

  // Auto-fetch on mount and when storeId changes
  useEffect(() => {
    if (autoFetch && storeId) {
      fetchMemories()
    }
  }, [autoFetch, storeId, fetchMemories])

  // Listen for WebSocket updates from LLM memory operations
  useEffect(() => {
    if (!storeId) return

    const handleStoreMemoryUpdate = (data: StoreMemoryUpdateData) => {
      // Only refresh if this update is for our store
      if (data.storeId === storeId) {
        console.log(`📡 Store memory updated via WebSocket (${data.action}):`, data)
        // Refresh the memories list
        fetchMemories()
      }
    }

    // Register the listener
    // Using the socket's native on method since socketService.on is typed for specific events
    const socket = (socketService as any).socket
    if (socket) {
      socket.on('store-memory:updated', handleStoreMemoryUpdate)
      console.log('🔌 Registered store-memory:updated listener for store:', storeId)
    }

    // Cleanup listener on unmount
    return () => {
      if (socket) {
        socket.off('store-memory:updated', handleStoreMemoryUpdate)
        console.log('🔌 Unregistered store-memory:updated listener')
      }
    }
  }, [storeId, fetchMemories])

  return {
    memories,
    loading,
    error,
    activeCount,
    maxNotes,
    canAddMore,
    categories,
    fetchMemories,
    createMemory,
    updateMemory,
    deleteMemory,
    refreshCount
  }
}

export default useStoreMemory
